import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import API from '../services/api';
import './DelegateSubmission.css';

const DelegateSubmission = () => {
  const { incidentId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [incident, setIncident] = useState({});
  const [submission, setSubmission] = useState({
    findings: '',
    recommendations: ''
  });
  
  // Add console logs to help debug
  console.log('Loading DelegateSubmission component for incident:', incidentId);
  
  useEffect(() => {
    const fetchIncidentDetails = async () => {
      try {
        setLoading(true);
        console.log('Fetching incident details for ID:', incidentId);
        
        const response = await API.get(`/api/incidents/${incidentId}/details`);
        console.log('API response:', response.data);
        
        if (!response.data.success) {
          throw new Error(response.data.message || 'Failed to load incident');
        }
        
        setIncident(response.data.incident || {});
        
        // Pre-fill form if there are existing findings/recommendations
        if (response.data.investigation && response.data.investigation.DELEGATE_FINDINGS) {
          setSubmission(prev => ({
            ...prev,
            findings: response.data.investigation.DELEGATE_FINDINGS
          }));
        }
        
        if (response.data.investigation && response.data.investigation.DELEGATE_RECOMMENDATIONS) {
          setSubmission(prev => ({
            ...prev,
            recommendations: response.data.investigation.DELEGATE_RECOMMENDATIONS
          }));
        }
        
        setLoading(false);
      } catch (err) {
        console.error('Error loading incident details:', err);
        setError(`Failed to load incident #${incidentId} details. Please try again later.`);
        setLoading(false);
      }
    };
    
    if (incidentId) {
      fetchIncidentDetails();
    }
  }, [incidentId]);
  
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setSubmission(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!submission.findings.trim() || !submission.recommendations.trim()) {
      alert('Please provide both findings and recommendations');
      return;
    }
    
    try {
      setLoading(true);
      const response = await API.post(`/api/incidents/${incidentId}/delegate-submit`, submission);
      
      if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to submit investigation');
      }
      
      alert('Your investigation has been submitted successfully');
      navigate('/delegate-tasks');
    } catch (err) {
      console.error('Error submitting investigation:', err);
      setError('Failed to submit investigation. Please try again.');
      setLoading(false);
    }
  };
  
  if (loading) {
    return <div className="loading">Loading incident details...</div>;
  }
  
  if (error) {
    return (
      <div className="error-container">
        <div className="error-message">{error}</div>
        <button 
          className="btn secondary"
          onClick={() => navigate('/delegate-tasks')}
        >
          Return to Dashboard
        </button>
      </div>
    );
  }

  // Empty object check - in case API returns success but no data
  if (!incident || Object.keys(incident).length === 0) {
    return (
      <div className="error-container">
        <div className="error-message">No incident data available. The incident may not exist or you may not have permission to view it.</div>
        <button 
          className="btn secondary"
          onClick={() => navigate('/delegate-tasks')}
        >
          Return to Dashboard
        </button>
      </div>
    );
  }
  
  return (
    <div className="delegate-submission">
      <h2>Delegate Investigation Form</h2>
      
      <div className="incident-details">
        <h3>Incident Information</h3>
        <div className="detail-grid">
          <div className="detail-item">
            <span className="label">Incident ID:</span>
            <span>{incident.ID}</span>
          </div>
          <div className="detail-item">
            <span className="label">Title:</span>
            <span>{incident.TITLE}</span>
          </div>
          <div className="detail-item">
            <span className="label">Date:</span>
            <span>{incident.DATE ? new Date(incident.DATE).toLocaleDateString() : 'N/A'}</span>
          </div>
          <div className="detail-item">
            <span className="label">Location:</span>
            <span>{incident.LOCATION || 'Not specified'}</span>
          </div>
        </div>
        
        <div className="description-box">
          <h4>Incident Description:</h4>
          <p>{incident.DESCRIPTION || 'No description provided.'}</p>
        </div>
        
        <div className="delegate-instructions">
          <h4>Instructions for Investigation:</h4>
          <p>{incident.DELEGATE_INSTRUCTIONS || 'No specific instructions provided.'}</p>
        </div>
      </div>
      
      <form onSubmit={handleSubmit} className="submission-form">
        <div className="form-group">
          <label htmlFor="findings">Investigation Findings:</label>
          <textarea
            id="findings"
            name="findings"
            value={submission.findings}
            onChange={handleInputChange}
            required
            placeholder="Describe what you discovered during your investigation..."
            rows={6}
          ></textarea>
          <small className="help-text">
            Include all relevant facts, observations, and data collected during your investigation.
          </small>
        </div>
        
        <div className="form-group">
          <label htmlFor="recommendations">Recommendations:</label>
          <textarea
            id="recommendations"
            name="recommendations"
            value={submission.recommendations}
            onChange={handleInputChange}
            required
            placeholder="What actions do you recommend to prevent similar incidents..."
            rows={6}
          ></textarea>
          <small className="help-text">
            Suggest specific actions that could prevent similar incidents in the future.
          </small>
        </div>
        
        <div className="form-actions">
          <button type="button" className="btn secondary" onClick={() => navigate('/delegate-tasks')}>
            Cancel
          </button>
          <button type="submit" className="btn primary">
            Submit Investigation
          </button>
        </div>
      </form>
    </div>
  );
};

export default DelegateSubmission;